/**
 * The roles that make someone Vera staff rather than a brand's user.
 *
 * ONE list, and it lives HERE — a module with no imports of its own — because both
 * `useIsInternalUser` and `useWorkOSAuth` need it and the first already imports the
 * second. Keeping it in the hook would make that a cycle (VRG-410).
 *
 * VRG-410 widened it from `platform_admin` + `platform_manager`. `vera_fulfillment` and
 * `vera_finance` (VRG-89) hold internal permissions the seed grants deliberately,
 * including `internal_company_enter_space`, which means nothing unless they can reach
 * the console. Which PAGES each one reaches is decided per-route by
 * `routes/internalAccess.ts`, not by editing this list.
 *
 * `vera_support` is DELIBERATELY EXCLUDED and it is not an oversight: it has no seeded
 * grants by design (`seed.ts:679-680`), because it is company-typed and its permission
 * set is granted per-company by the brand. It is the in-SPACE identity a visiting
 * staffer operates under, never a role a person is assigned. Admitting it would open a
 * console with nothing in it.
 */
export const INTERNAL_ROLE_NAMES: ReadonlySet<string> = new Set([
  'platform_admin',
  'platform_manager',
  'vera_fulfillment',
  'vera_finance',
])

/**
 * Role-name half of the "is this Vera staff" predicate, for call sites that have a raw
 * role name and no hook (redirect helpers, route guards).
 *
 * ⚠️ Feed it the RAW role name from the WorkOS profile, never `useAuth().profile.role.name`.
 * `mapWorkOSUserToProfile` maps through a four-entry table and falls back to
 * `'company_team_member'`, so a `vera_fulfillment` user reads as a company member there.
 */
export const isInternalRoleName = (roleName?: string | null): boolean =>
  !!roleName && INTERNAL_ROLE_NAMES.has(roleName)

export type RoleStatusKey = 'default' | 'modified' | 'custom'

export const getRoleStatus = (
  role: { isCustom?: boolean; isOverridden?: boolean },
): RoleStatusKey => (role.isOverridden ? 'modified' : role.isCustom ? 'custom' : 'default')

export const ROLES = {
  platform_admin: 'Admin',
  platform_manager: 'Account Manager',
  company_admin: 'Admin',
  company_team_member: 'Member',
}

// ── Canonical role display-name source of truth ──────────────────────────────
// Backend role slug → human display name. The list table, the member Role column,
// the assign-role dropdown, and the permissions matrix all read from this so a
// role reads the same everywhere (e.g. `company_admin` → "Admin", never the raw
// "Company Admin" title-cased slug). Internal/Vera roles get the "Vera …" label.
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  platform_admin: 'Vera Admin',
  // VRG-89: the M:N-assigned Vera staffer (CS / owner of assigned companies).
  platform_manager: 'Account Manager',
  // VRG-89: new internal Vera-staff roles.
  vera_fulfillment: 'Fulfillment',
  vera_finance: 'Finance',
  // VRG-90: the brand's grant column — generic "Vera support", NOT an internal role
  // name, since all internal roles share this one in-space permission set.
  vera_support: 'Vera support',
  company_admin: 'Admin',
  company_team_member: 'Member',
  company_finance: 'Finance',
  company_design: 'Design',
  company_analytics: 'Analytics',
  company_marketing: 'Marketing',
  company_logistics: 'Logistics',
  company_purchasing: 'Purchasing',
  company_engineering: 'Engineering',
  company_legal: 'Legal',
  // VRG-565: shop / stockroom staff who confirm arrivals by scanning in the VERA phone
  // app. APP-ONLY — they can never sign into this console, but the role still needs a
  // display name HERE: an admin assigns it, sees it in the Users table's Role column, and
  // reads its row on the permissions matrix. Without an entry the slug would fall through
  // to titleCaseSlug() and render "Company Location Manager". 16 chars, inside §25's 22.
  company_location_manager: 'Location manager',
}

// Back-compat alias — built-in company labels are a subset of ROLE_DISPLAY_NAMES.
export const BUILTIN_ROLE_LABELS: Record<string, string> = ROLE_DISPLAY_NAMES

/**
 * The built-in COMPANY roles a brand's people can actually hold, in the order the
 * roles screen lists them. Admin and Member first (the two everyone has), then the
 * functional roles alphabetically.
 *
 * Used by the announcement audience picker (VRG-332), which targets role NAMES. It is
 * this list and not the whole `roles` table because the other three groups are not
 * audiences you would announce to: `platform_*` / `vera_*` are Vera staff (who read the
 * internal surfaces, not the changelog), `vera_support` is a per-company GRANT rather
 * than an assignable role, and `owner` is a certificate owner, not a platform user.
 *
 * Built-ins ONLY, and that is the gate's accepted limitation rather than an oversight: a
 * company's own standalone custom role has a name Vera cannot know at authoring time,
 * so it can never appear here and will not match a gate. See migration 0074.
 */
export const COMPANY_ROLE_NAMES = [
  'company_admin',
  'company_team_member',
  'company_analytics',
  'company_design',
  'company_engineering',
  'company_finance',
  'company_legal',
  'company_logistics',
  'company_marketing',
  'company_purchasing',
  // VRG-565: `company_location_manager` is DELIBERATELY ABSENT. This list is the
  // announcement audience picker's options, and an announcement is read in the console's
  // "What's new" — a surface a Location manager can never reach (APP_ONLY_ROLE_NAMES).
  // Offering it as an audience would let someone target a group that cannot receive.
] as const

// Title-Case a backend slug as a fallback when there's no mapped display name
// (custom roles store their own already-human name).
const titleCaseSlug = (slug: string): string =>
  slug
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

// Human-friendly display name for a company role: the mapped label when known,
// otherwise a Title-Cased version of the stored slug (custom roles store their own name).
export function getRoleDisplayName(role: { name: string; isCustom: boolean }): string {
  if (!role.isCustom && ROLE_DISPLAY_NAMES[role.name]) {
    return ROLE_DISPLAY_NAMES[role.name]
  }
  return titleCaseSlug(role.name)
}

// Display name for a member's assigned role, keyed on the raw backend slug
// (e.g. the Users table Role column, where we only have `role.name`).
export function getMemberRoleDisplayName(roleName?: string | null): string {
  if (!roleName) return '—'
  return ROLE_DISPLAY_NAMES[roleName] ?? titleCaseSlug(roleName)
}

export const DEFAULT_PERMISSIONS = [ 
    "GENERAL.ALL",
    "GENERAL.VIEW",
    "GENERAL.EDIT",
    "GENERAL.CREATE",
    "GENERAL.DELETE",
    "USERS.ALL",
    "USERS.VIEW",
    "USERS.EDIT",
    "USERS.CREATE",
    "USERS.DELETE",
    "USERS.INVITE",
    "ROLES.ALL",
    "ROLES.VIEW",
    "ROLES.EDIT",
    "ROLES.CREATE",
    "ROLES.DELETE",
    "ROLES.ASSIGN",
    "INSTALLATION.ALL",
    "INSTALLATION.VIEW",
    "INSTALLATION.EDIT",
    "BILLING.ALL",
    "BILLING.ACTIVATION_SETTINGS",
    "BILLING.CHANGE_PLAN",
    "BILLING.END_PLAN",
    "INVOICES.VIEW",
    "INVOICES.EXPORT",
    "PAYMENT_METHODS.VIEW",
    "PAYMENT_METHODS.EDIT",
    "PAYMENT_METHODS.CREATE",
    "PAYMENT_METHODS.DELETE",
    "ADDRESS_BOOK.ALL",
    "ADDRESS_BOOK.VIEW",
    "ADDRESS_BOOK.EDIT",
    "ADDRESS_BOOK.CREATE",
    "ADDRESS_BOOK.DELETE",
    "INTEGRATIONS.ALL",
    "INTEGRATIONS.VIEW",
    "INTEGRATIONS.EDIT",
    "INTEGRATIONS.CREATE",
    "INTEGRATIONS.DELETE",
    "ACTIVITY_LOG.ALL",
    "ACTIVITY_LOG.VIEW",
    "ACTIVITY_LOG.EXPORT",
    "FIELD_MAPPING.ALL",
    "FIELD_MAPPING.VIEW",
    "FIELD_MAPPING.EDIT",
    "FIELD_MAPPING.CREATE",
    "FIELD_MAPPING.DELETE",
    // MAIN
    // "MAIN.ALL",
    // "MAIN.VIEW",
    // "MAIN.EDIT",
    // "MAIN.CREATE",
    // "MAIN.DELETE",
    // "MAIN.IMPORT",
    // "MAIN.EXPORT",
    "TAGS.ALL",
    "TAGS_INVENTORY.VIEW",
    "TAGS_INVENTORY.EDIT",
    "TAGS_SHOP.VIEW",
    "TAGS_SHOP.PURCHASE",
    "TAGS_ORDERS.VIEW",
    "TAGS_ORDERS.EXPORT",
    "PRODUCTS.ALL",
    "PRODUCTS.VIEW",
    "PRODUCTS.EDIT",
    "PRODUCTS.CREATE",
    "PRODUCTS.DELETE",
    "PRODUCTS.IMPORT",
    "PRODUCTS.EXPORT",
    "CERTIFICATES.ALL",
    "CERTIFICATES.VIEW",
    "CERTIFICATES.EDIT",
    "CERTIFICATES.CREATE",
    "CERTIFICATES.DELETE",
    "CERTIFICATES.REPLACE_TAG",
    "CERTIFICATES.TRANSFER",
    "CERTIFICATES.EXPORT",
    "OWNERS.ALL",
    "OWNERS.VIEW",
    "OWNERS.EDIT",
    "OWNERS.CREATE",
    "OWNERS.DELETE",
    "OWNERS.IMPORT",
    "OWNERS.EXPORT",
    "ANALYTICS.ALL",
    "ANALYTICS.GENERAL",
    // Add all roles tab permissions
    "ANALYTICS.LOGISTICS",
    "ANALYTICS.EXPORT",
    "STUDIO.ALL",
    "STUDIO.VIEW",
    "STUDIO.EDIT",
    "STUDIO.CREATE",
    "STUDIO.DELETE",
    "FEATURES.ALL",
    "FEATURES.VIEW",
    "FEATURES.EDIT",
    // INTERNAL
    "INTERNAL_ANALYTICS.VIEW",
    "INTERNAL_ANALYTICS.EXPORT",
    "INTERNAL_COMPANIES.VIEW",
    "INTERNAL_COMPANIES.EDIT",
    "INTERNAL_COMPANIES.CREATE",
    "INTERNAL_COMPANIES.DELETE",
    "INTERNAL_COMPANIES.EXPORT",
    "INTERNAL_COMPANIES.ENTER_SPACE",
    "INTERNAL_PACKAGES.VIEW",
    "INTERNAL_PACKAGES.UPLOAD",
    "INTERNAL_PACKAGES.EXPORT",
    "INTERNAL_ORDERS.VIEW",
    "INTERNAL_ORDERS.LINK_PACKAGE",
    "INTERNAL_ORDERS.EXPORT",
    "INTERNAL_INVOICE.VIEW",
    "INTERNAL_INVOICE.EDIT",
    "INTERNAL_INVOICE.CREATE",
    "INTERNAL_INVOICE.DELETE",
    "INTERNAL_INVOICE.EXPORT",
    "INTERNAL_USERS.VIEW",
    "INTERNAL_USERS.EDIT",
    "INTERNAL_USERS.CREATE",
    "INTERNAL_USERS.DELETE",
]

export const DEFAULT_ROLES = {
  vera_admin: {
    name: 'Vera admin',
    permissions: [
      "MAIN.ALL",
      "SETTINGS.ALL",
      "INTERNAL.ALL",
    ]
  },
  vera_manager: {
    name:'Vera manager',
    permissions: [
      
    ]
  },
  // owner: {
  //   name:'Owner',
  //   permissions: [
  //     "SETTINGS.ALL",
  //     "TAGS.ALL",
  //     "PRODUCTS.ALL",
  //   ]
  // },
  admin: {
    name:'Admin',
    permissions: [
      "SETTINGS.ALL",
      "SETTINGS.VIEW",
      "SETTINGS.EDIT",
      "SETTINGS.CREATE",
      "SETTINGS.DELETE",
      "SETTINGS.IMPORT",
      "SETTINGS.EXPORT",
      "GENERAL.ALL",
      "GENERAL.VIEW",
      "GENERAL.EDIT",
      "GENERAL.CREATE",
      "GENERAL.DELETE",
      "USERS.ALL",
      "USERS.VIEW",
      "USERS.EDIT",
      "USERS.CREATE",
      "USERS.DELETE",
      "USERS.INVITE",
      "ROLES.ALL",
      "ROLES.VIEW",
      "ROLES.EDIT",
      "ROLES.CREATE",
      "ROLES.DELETE",
      "ROLES.ASSIGN",
      "INSTALLATION.ALL",
      "INSTALLATION.VIEW",
      "INSTALLATION.EDIT",
      "BILLING.ALL",
      "BILLING.CHANGE_PLAN",
      "BILLING.END_PLAN",
      "FEATURES.ACTIVATION_SETTINGS",
      "INVOICES.VIEW",
      "INVOICES.EXPORT",
      "PAYMENT_METHODS.VIEW",
      "PAYMENT_METHODS.EDIT",
      "PAYMENT_METHODS.CREATE",
      "PAYMENT_METHODS.DELETE",
      "ADDRESS_BOOK.ALL",
      "ADDRESS_BOOK.VIEW",
      "ADDRESS_BOOK.EDIT",
      "ADDRESS_BOOK.CREATE",
      "ADDRESS_BOOK.DELETE",
      "INTEGRATIONS.ALL",
      "INTEGRATIONS.VIEW",
      "INTEGRATIONS.EDIT",
      "INTEGRATIONS.CREATE",
      "INTEGRATIONS.DELETE",
      "ACTIVITY_LOG.ALL",
      "ACTIVITY_LOG.VIEW",
      "ACTIVITY_LOG.EXPORT",
      "FIELD_MAPPING.ALL",
      "FIELD_MAPPING.VIEW",
      "FIELD_MAPPING.EDIT",
      "FIELD_MAPPING.CREATE",
      "FIELD_MAPPING.DELETE",
      "TAGS.ALL",
      "TAGS_INVENTORY.VIEW",
      "TAGS_INVENTORY.EDIT",
      "TAGS_SHOP.VIEW",
      "TAGS_SHOP.PURCHASE",
      "TAGS_ORDERS.VIEW",
      "TAGS_ORDERS.EXPORT",
      "PRODUCTS.ALL",
      "PRODUCTS.VIEW",
      "PRODUCTS.EDIT",
      "PRODUCTS.CREATE",
      "PRODUCTS.DELETE",
      "PRODUCTS.IMPORT",
      "PRODUCTS.EXPORT",
      "CERTIFICATES.ALL",
      "CERTIFICATES.VIEW",
      "CERTIFICATES.EDIT",
      "CERTIFICATES.CREATE",
      "CERTIFICATES.DELETE",
      "CERTIFICATES.REPLACE_TAG",
      "CERTIFICATES.TRANSFER",
      "CERTIFICATES.EXPORT",
      "OWNERS.ALL",
      "OWNERS.VIEW",
      "OWNERS.EDIT",
      "OWNERS.CREATE",
      "OWNERS.DELETE",
      "OWNERS.IMPORT",
      "OWNERS.EXPORT",
      "ANALYTICS.ALL",
      "ANALYTICS.GENERAL",
      "ANALYTICS.LOGISTICS",
      "ANALYTICS.EXPORT",
      "STUDIO.ALL",
      "STUDIO.VIEW",
      "STUDIO.EDIT",
      "STUDIO.CREATE",
      "STUDIO.DELETE",
      "FEATURES.ALL",
      "FEATURES.VIEW",
      "FEATURES.EDIT",
    ]
  },
  member: {
    name:'Member',
    permissions: [
      "ADDRESS_BOOK.VIEW",
      "INTEGRATIONS.VIEW",
      "FIELD_MAPPING.VIEW",
      "ACTIVITY_LOG.VIEW",
      "TAGS.ALL",
      "TAGS_INVENTORY.VIEW",
      "TAGS_INVENTORY.EDIT",
      "TAGS_SHOP.VIEW",
      "TAGS_ORDERS.VIEW",
      "TAGS_ORDERS.EXPORT",
      "PRODUCTS.ALL",
      "PRODUCTS.VIEW",
      "PRODUCTS.EDIT",
      "PRODUCTS.CREATE",
      "PRODUCTS.DELETE",
      "PRODUCTS.IMPORT",
      "PRODUCTS.EXPORT",
      "CERTIFICATES.VIEW",
      "CERTIFICATES.CREATE",
      "CERTIFICATES.EDIT",
      "CERTIFICATES.TRANSFER",
      "OWNERS.VIEW",
      "OWNERS.EDIT",
      "OWNERS.CREATE",
      "OWNERS.DELETE",
      "OWNERS.IMPORT",
      "ANALYTICS.GENERAL",
      "ANALYTICS.LOGISTICS",
      "ANALYTICS.EXPORT",
      "STUDIO.ALL",
      "STUDIO.VIEW",
      "STUDIO.EDIT",
      "STUDIO.CREATE",
      "STUDIO.DELETE",
      "FEATURES.VIEW",
    ]
  },
  finance: {
    name:'Finance',
    permissions: [
      "BILLING.ALL",
      "BILLING.ACTIVATION_SETTINGS",
      "BILLING.CHANGE_PLAN",
      "BILLING.END_PLAN",
      "INVOICES.VIEW",
      "INVOICES.EXPORT",
      "PAYMENT_METHODS.VIEW",
      "PAYMENT_METHODS.EDIT",
      "PAYMENT_METHODS.CREATE",
      "PAYMENT_METHODS.DELETE",
      "ADDRESS_BOOK.VIEW",
      "INTEGRATIONS.VIEW",
      "FIELD_MAPPING.VIEW",
      "ACTIVITY_LOG.ALL",
      "ACTIVITY_LOG.VIEW",
      "ACTIVITY_LOG.EXPORT",
      "TAGS.ALL",
      "TAGS_INVENTORY.VIEW",
      "TAGS_INVENTORY.EDIT",
      "TAGS_SHOP.VIEW",
      "TAGS_SHOP.PURCHASE",
      "TAGS_ORDERS.VIEW",
      "TAGS_ORDERS.EXPORT",
      "PRODUCTS.VIEW",
      "CERTIFICATES.VIEW",
      "OWNERS.VIEW",
      "ANALYTICS.VIEW",
      "FEATURES.VIEW",
    ]
  },
  legal: {
    name: 'Legal',
    permissions: [
      
    ]
  },
  marketing: {
    name:'Marketing',
    permissions: [
      
    ]
  },
  purchasing: {
    name: 'Purchasing',
    permissions: [
      // VRG-119: Purchasing can view + purchase tags (mirrors the backend seed grant).
      "TAGS_SHOP.VIEW",
      "TAGS_SHOP.PURCHASE",
      "TAGS_ORDERS.VIEW",
      
    ]
  },
  design: {
    name: 'Design',
    permissions: [
      
    ]
  },
  analysis: {
    name:'Analysis',
    permissions: [
      
    ]
  },
  development: {
    name: 'Development',
    permissions: [
      
    ]
  },
  logistics: { 
    name: 'Logistics',
    permissions: [
      
    ]
  },
}